import axios from 'axios';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and business context to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add business context header for multi-tenant isolation
    const activeBusiness = localStorage.getItem('active_business');
    if (activeBusiness) {
      try {
        const business = JSON.parse(activeBusiness);
        if (business.id) {
          config.headers['X-Business-Id'] = business.id;
        }
      } catch (e) {
        console.error('Failed to parse active_business from localStorage:', e);
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token: newRefreshToken } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('active_business');
        localStorage.removeItem('active_business_id');
        localStorage.removeItem('selectedBusinessId');
        localStorage.removeItem('user_businesses');
        localStorage.removeItem('user_type');
        localStorage.removeItem('staff_info');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden
    // NOTE: Don't toast here -- let the calling code handle it via getApiErrorMessage.
    // Double-toasting occurs when both the interceptor and the catch block show errors.

    // Handle 500+ Server Errors
    if (error.response?.status >= 500) {
      toast.error('Something went wrong. Please try again.');
    }

    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  sendOTP: (phoneOrEmail, purpose = 'verification') =>
    api.post('/auth/send-otp', { phone_or_email: phoneOrEmail, purpose }),
  verifyOTP: (phoneOrEmail, otp) =>
    api.post('/auth/verify-otp', { phone_or_email: phoneOrEmail, otp }),
  forgotPassword: (phoneOrEmail) =>
    api.post('/auth/forgot-password', { phone_or_email: phoneOrEmail }),
  resetPassword: (phoneOrEmail, otp, newPassword) =>
    api.post('/auth/reset-password', {
      phone_or_email: phoneOrEmail,
      otp,
      new_password: newPassword,
    }),
};

// User API
export const userAPI = {
  getMe: () => api.get('/users/me'),
};

// Business API
export const businessAPI = {
  list: () => api.get('/businesses'),
  get: (businessId) => api.get(`/businesses/${businessId}`),
  create: (data) => api.post('/businesses', data),
  update: (businessId, data) => api.patch(`/businesses/${businessId}`, data),
  getMembers: (businessId) => api.get(`/businesses/${businessId}/members`),
  getFeatures: (businessId) => api.get(`/businesses/${businessId}/features`),
  getPrintSettings: (businessId) => api.get(`/businesses/${businessId}/print-settings`),
  updatePrintSettings: (businessId, settings) => api.put(`/businesses/${businessId}/print-settings`, settings),
};

// Plan API
export const planAPI = {
  list: () => api.get('/plans'),
  getSubscription: (businessId) =>
    api.get(`/plans/businesses/${businessId}/subscription`),
  changePlan: (businessId, data) =>
    api.post(`/plans/businesses/${businessId}/subscription/change-plan`, data),
  cancelSubscription: (businessId) =>
    api.post(`/plans/businesses/${businessId}/subscription/cancel`),
};

// Role API
export const roleAPI = {
  list: (scope = 'business', businessId = null) => {
    const params = { scope };
    if (businessId) params.business_id = businessId;
    return api.get('/roles', { params });
  },
  getPermissions: () => api.get('/roles/permissions'),
  create: (businessId, data) =>
    api.post(`/roles/businesses/${businessId}/roles`, data),
};